import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService, optionsService, registroConfigService } from '../services/api';
import { sanitizePhone } from '../utils/phone';
import { normalizeDni } from '../utils/dni';
import ConfirmPopup from '../components/ConfirmPopup';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const [registroDeshabilitado, setRegistroDeshabilitado] = useState(false);
  const [mensajeDeshabilitado, setMensajeDeshabilitado] = useState('');
  const [loadingRegistroConfig, setLoadingRegistroConfig] = useState(true);
  const [currentSection, setCurrentSection] = useState(1);
  const [formData, setFormData] = useState({
    // Datos básicos
    email: '',
    first_name: '',
    last_name: '',
    // Datos personales
    dni: '',
    genero_id: '',
    fecha_nacimiento: '',
    telefono_celular: '',
    pais_id: '',
    pais_otro: '',
    provincia_id: '',
    provincia_otro: '',
    localidad_id: '', // Partido (se envía como localidad_id al backend)
    localidad_otro: '',
    barrio_id: '',
    barrio_otro: '',
    // Estudio y Trabajo
    tipo_educacion_secundaria_id: '',
    nivel_estudio_id: '',
    situacion_laboral_id: '',
    area_trabajo_id: '',
    habilidades_ids: [],
    // Información adicional
    objetivo_curso_id: '',
    horas_dedicacion_id: '',
    tiene_computadora_internet: '',
    fuente_informacion_id: '',
  });
  
  const [options, setOptions] = useState({
    generos: [],
    paises: [],
    provincias: [],
    localidades: [], // Partidos (misma API que localidades)
    barrios: [],
    tiposEducacionSecundaria: [],
    nivelesEstudio: [],
    situacionesLaborales: [],
    areasTrabajo: [],
    habilidades: [],
    objetivosCurso: [],
    horasDedicacion: [],
    fuentesInformacion: [],
  });
  
  const [error, setError] = useState('');
  const [invalidFields, setInvalidFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [selectedSituacionLaboral, setSelectedSituacionLaboral] = useState(null);
  // Habilidades "Otros": lista de textos para obtener/crear en el backend
  const [otrasHabilidades, setOtrasHabilidades] = useState(['']);

  // Nombres amigables para mostrar en el mensaje de error (todos los campos obligatorios)
  const fieldLabels = {
    first_name: 'Nombre/s',
    last_name: 'Apellido/s',
    genero_id: 'Género',
    fecha_nacimiento: 'Fecha de Nacimiento',
    telefono_celular: 'Teléfono Celular',
    pais_id: 'País',
    pais_otro: 'País (si elegís Otro)',
    provincia_id: 'Provincia',
    provincia_otro: 'Provincia (si elegís Otro)',
    localidad_id: 'Partido',
    localidad_otro: 'Partido (si elegís Otro)',
    barrio_id: 'Barrio',
    barrio_otro: 'Barrio (si elegís Otro)',
    email: 'Email',
    dni: 'DNI',
    tipo_educacion_secundaria_id: 'Dónde estudiaste la Secundaria',
    nivel_estudio_id: 'Máximo nivel de estudio',
    situacion_laboral_id: 'Situación laboral',
    area_trabajo_id: 'Área de trabajo',
    habilidades_ids: 'Al menos una habilidad',
    habilidades_otros: 'Si elegís Otros, ingresá al menos una habilidad',
    objetivo_curso_id: 'Objetivo del curso',
    horas_dedicacion_id: 'Horas por semana',
    tiene_computadora_internet: '¿Computadora e internet?',
    fuente_informacion_id: '¿Cómo te enteraste?',
  };

  // Comprobar si el registro está habilitado (config pública)
  useEffect(() => {
    let cancelled = false;
    registroConfigService
      .get()
      .then((data) => {
        if (!cancelled && data.registro_habilitado === false) {
          setRegistroDeshabilitado(true);
          setMensajeDeshabilitado(data.mensaje || 'Temporalmente el registro se encuentra deshabilitado');
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingRegistroConfig(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Cargar opciones al montar el componente
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [
          generos,
          paises,
          tiposEducacionSecundaria,
          nivelesEstudio,
          situacionesLaborales,
          areasTrabajo,
          habilidades,
          objetivosCurso,
          horasDedicacion,
          fuentesInformacion,
        ] = await Promise.all([
          optionsService.getGeneros(),
          optionsService.getPaises(),
          optionsService.getTiposEducacionSecundaria(),
          optionsService.getNivelesEstudio(),
          optionsService.getSituacionesLaborales(),
          optionsService.getAreasTrabajo(),
          optionsService.getHabilidades(),
          optionsService.getObjetivosCurso(),
          optionsService.getHorasDedicacion(),
          optionsService.getFuentesInformacion(),
        ]);

        setOptions({
          generos,
          paises,
          provincias: [],
          localidades: [],
          barrios: [],
          tiposEducacionSecundaria,
          nivelesEstudio,
          situacionesLaborales,
          areasTrabajo,
          habilidades,
          objetivosCurso,
          horasDedicacion,
          fuentesInformacion,
        });
        setLoadingOptions(false);
      } catch (err) {
        console.error('Error al cargar opciones:', err);
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  // Cargar provincias cuando se selecciona un país (no "Otro")
  useEffect(() => {
    if (formData.pais_id && formData.pais_id !== 'otro') {
      optionsService.getProvincias(formData.pais_id)
        .then(provincias => {
          setOptions(prev => ({ ...prev, provincias, localidades: [], barrios: [] }));
          setFormData(prev => ({ ...prev, provincia_id: '', provincia_otro: '', localidad_id: '', localidad_otro: '', barrio_id: '', barrio_otro: '' }));
        })
        .catch(() => setOptions(prev => ({ ...prev, provincias: [], localidades: [], barrios: [] })));
    } else {
      setOptions(prev => ({ ...prev, provincias: [], localidades: [], barrios: [] }));
      setFormData(prev => ({ ...prev, provincia_id: '', provincia_otro: '', localidad_id: '', localidad_otro: '', barrio_id: '', barrio_otro: '' }));
    }
  }, [formData.pais_id]);

  // Cargar partidos (localidades) cuando se selecciona una provincia (no "Otro")
  useEffect(() => {
    if (formData.provincia_id && formData.provincia_id !== 'otro') {
      optionsService.getLocalidades(formData.provincia_id)
        .then(localidades => {
          setOptions(prev => ({ ...prev, localidades, barrios: [] }));
          setFormData(prev => ({ ...prev, localidad_id: '', localidad_otro: '', barrio_id: '', barrio_otro: '' }));
        })
        .catch(() => setOptions(prev => ({ ...prev, localidades: [], barrios: [] })));
    } else {
      setOptions(prev => ({ ...prev, localidades: [], barrios: [] }));
      setFormData(prev => ({ ...prev, localidad_id: '', localidad_otro: '', barrio_id: '', barrio_otro: '' }));
    }
  }, [formData.provincia_id]);

  // Cargar barrios cuando se selecciona un partido (no "Otro")
  useEffect(() => {
    if (formData.localidad_id && formData.localidad_id !== 'otro') {
      optionsService.getBarrios(formData.localidad_id)
        .then(barrios => {
          setOptions(prev => ({ ...prev, barrios }));
          setFormData(prev => ({ ...prev, barrio_id: '', barrio_otro: '' }));
        })
        .catch(() => setOptions(prev => ({ ...prev, barrios: [] })));
    } else {
      setOptions(prev => ({ ...prev, barrios: [] }));
      setFormData(prev => ({ ...prev, barrio_id: '', barrio_otro: '' }));
    }
  }, [formData.localidad_id]);

  // Actualizar situación laboral seleccionada
  useEffect(() => {
    if (formData.situacion_laboral_id) {
      const situacion = options.situacionesLaborales.find(
        s => s.id === parseInt(formData.situacion_laboral_id)
      );
      setSelectedSituacionLaboral(situacion);
      if (!situacion?.requiere_area) {
        setFormData(prev => ({ ...prev, area_trabajo_id: '' }));
      }
    } else {
      setSelectedSituacionLaboral(null);
    }
  }, [formData.situacion_laboral_id, options.situacionesLaborales]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'habilidades_ids') {
        const habilidadId = parseInt(value);
        setFormData(prev => ({
          ...prev,
          habilidades_ids: prev.habilidades_ids.includes(habilidadId)
            ? prev.habilidades_ids.filter(id => id !== habilidadId)
            : [...prev.habilidades_ids, habilidadId]
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      const finalValue = name === 'telefono_celular'
        ? sanitizePhone(value)
        : name === 'dni'
          ? normalizeDni(value)
          : value;
      setFormData(prev => ({ ...prev, [name]: finalValue }));
    }
    setError('');
    setInvalidFields(prev => prev.filter(f => f !== name));
  };

  const getRequiredErrors = () => {
    const err = [];
    if (!formData.first_name?.trim()) err.push('first_name');
    if (!formData.last_name?.trim()) err.push('last_name');
    if (!formData.genero_id) err.push('genero_id');
    if (!formData.fecha_nacimiento?.trim()) err.push('fecha_nacimiento');
    if (!formData.telefono_celular?.trim()) err.push('telefono_celular');
    const hasPais = formData.pais_id || (formData.pais_otro || '').trim();
    const hasProvincia = formData.provincia_id || (formData.provincia_otro || '').trim();
    const hasLocalidad = formData.localidad_id || (formData.localidad_otro || '').trim();
    const hasBarrio = formData.barrio_id || (formData.barrio_otro || '').trim();
    if (!hasPais) err.push('pais_id');
    if (formData.pais_id === 'otro' && !(formData.pais_otro || '').trim()) err.push('pais_otro');
    if (!hasProvincia) err.push('provincia_id');
    if (formData.provincia_id === 'otro' && !(formData.provincia_otro || '').trim()) err.push('provincia_otro');
    if (!hasLocalidad) err.push('localidad_id');
    if (formData.localidad_id === 'otro' && !(formData.localidad_otro || '').trim()) err.push('localidad_otro');
    if (!hasBarrio) err.push('barrio_id');
    if (formData.barrio_id === 'otro' && !(formData.barrio_otro || '').trim()) err.push('barrio_otro');
    if (!formData.email?.trim()) err.push('email');
    if (!formData.dni?.trim()) err.push('dni');
    if (!formData.tipo_educacion_secundaria_id) err.push('tipo_educacion_secundaria_id');
    if (!formData.nivel_estudio_id) err.push('nivel_estudio_id');
    if (!formData.situacion_laboral_id) err.push('situacion_laboral_id');
    if (selectedSituacionLaboral?.requiere_area && !formData.area_trabajo_id) err.push('area_trabajo_id');
    if (!formData.habilidades_ids?.length) err.push('habilidades_ids');
    const otrosId = options.habilidades?.find(h => (h.nombre || '').toLowerCase() === 'otros')?.id;
    const otrosSeleccionado = otrosId && formData.habilidades_ids.includes(otrosId);
    if (otrosSeleccionado && otrasHabilidades.every(t => !(t && t.trim()))) err.push('habilidades_otros');
    if (!formData.objetivo_curso_id) err.push('objetivo_curso_id');
    if (!formData.horas_dedicacion_id) err.push('horas_dedicacion_id');
    if (formData.tiene_computadora_internet === '' || formData.tiene_computadora_internet === null || formData.tiene_computadora_internet === undefined) err.push('tiene_computadora_internet');
    if (!formData.fuente_informacion_id) err.push('fuente_informacion_id');
    return err;
  };

  // Sección donde aparece cada campo (para ir a la sección correcta al haber error)
  const fieldSection = {
    first_name: 1, last_name: 1, genero_id: 1, fecha_nacimiento: 1, telefono_celular: 1,
    pais_id: 1, pais_otro: 1, provincia_id: 1, provincia_otro: 1, localidad_id: 1, localidad_otro: 1, barrio_id: 1, barrio_otro: 1, email: 1, dni: 1,
    tipo_educacion_secundaria_id: 2, nivel_estudio_id: 2, situacion_laboral_id: 2, area_trabajo_id: 2, habilidades_ids: 2, habilidades_otros: 2,
    objetivo_curso_id: 3, horas_dedicacion_id: 3, tiene_computadora_internet: 3, fuente_informacion_id: 3,
  };

  const handleNext = () => {
    if (currentSection < 3) {
      setCurrentSection(currentSection + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInvalidFields([]);
    setLoading(true);

    const missing = getRequiredErrors();
    if (missing.length > 0) {
      setInvalidFields(missing);
      const labels = missing.map(f => fieldLabels[f] || f);
      setError(`Faltan campos por completar: ${labels.join(', ')}.`);
      setLoading(false);
      const firstSection = Math.min(...missing.map(f => fieldSection[f] || 1));
      setCurrentSection(firstSection);
      return;
    }

    // Resolver "Otros": obtener o crear habilidades personalizadas y armar lista final de IDs
    const otrosId = options.habilidades?.find(h => (h.nombre || '').toLowerCase() === 'otros')?.id;
    let habilidadesIdsFinal = [...(formData.habilidades_ids || [])];
    if (otrosId && formData.habilidades_ids.includes(otrosId)) {
      const textos = otrasHabilidades.filter(t => t && t.trim());
      const customIds = [];
      for (const nombre of textos) {
        try {
          const { id } = await optionsService.getOrCreateHabilidad(nombre);
          customIds.push(id);
        } catch (err) {
          setError(err.response?.data?.nombre?.[0] || 'Error al guardar una habilidad. Intentá de nuevo.');
          setLoading(false);
          return;
        }
      }
      // Quitar "Otros" y agregar los IDs de las habilidades personalizadas
      habilidadesIdsFinal = formData.habilidades_ids.filter(id => id !== otrosId).concat(customIds);
    }

    // Preparar datos para enviar (convertir strings vacíos a null; los _otro se mantienen como '' cuando vacíos para no enviar null)
    const submitData = { ...formData, habilidades_ids: habilidadesIdsFinal };
    if (submitData.dni) submitData.dni = normalizeDni(submitData.dni);
    const otrosKeys = ['pais_otro', 'provincia_otro', 'localidad_otro', 'barrio_otro'];
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '' && !otrosKeys.includes(key)) {
        submitData[key] = null;
      }
    });

    // Convertir IDs a enteros; si es "otro" enviar null y el texto en _otro
    if (submitData.genero_id) submitData.genero_id = parseInt(submitData.genero_id);
    if (submitData.pais_id === 'otro') submitData.pais_id = null;
    else if (submitData.pais_id) submitData.pais_id = parseInt(submitData.pais_id);
    if (submitData.provincia_id === 'otro') submitData.provincia_id = null;
    else if (submitData.provincia_id) submitData.provincia_id = parseInt(submitData.provincia_id);
    if (submitData.localidad_id === 'otro') submitData.localidad_id = null;
    else if (submitData.localidad_id) submitData.localidad_id = parseInt(submitData.localidad_id);
    if (submitData.barrio_id && submitData.barrio_id !== 'otro') {
      submitData.barrio_id = parseInt(submitData.barrio_id);
    } else {
      submitData.barrio_id = null;
      submitData.barrio_otro = (submitData.barrio_otro || '').trim();
    }
    if (submitData.tipo_educacion_secundaria_id) submitData.tipo_educacion_secundaria_id = parseInt(submitData.tipo_educacion_secundaria_id);
    if (submitData.nivel_estudio_id) submitData.nivel_estudio_id = parseInt(submitData.nivel_estudio_id);
    if (submitData.situacion_laboral_id) submitData.situacion_laboral_id = parseInt(submitData.situacion_laboral_id);
    if (submitData.area_trabajo_id) submitData.area_trabajo_id = parseInt(submitData.area_trabajo_id);
    if (submitData.objetivo_curso_id) submitData.objetivo_curso_id = parseInt(submitData.objetivo_curso_id);
    if (submitData.horas_dedicacion_id) submitData.horas_dedicacion_id = parseInt(submitData.horas_dedicacion_id);
    if (submitData.fuente_informacion_id) submitData.fuente_informacion_id = parseInt(submitData.fuente_informacion_id);
    if (submitData.tiene_computadora_internet !== null && submitData.tiene_computadora_internet !== '') {
      submitData.tiene_computadora_internet = submitData.tiene_computadora_internet === 'true' || submitData.tiene_computadora_internet === true;
    }

    try {
      await authService.register(submitData);
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = 
        err.response?.data?.email?.[0] ||
        err.response?.data?.password?.[0] ||
        err.response?.data?.non_field_errors?.[0] ||
        Object.values(err.response?.data || {}).flat()[0] ||
        'Error al registrar. Por favor, intenta nuevamente.';
      setError(errorMessage);
      // Volver a la primera sección si hay error
      setCurrentSection(1);
    } finally {
      setLoading(false);
    }
  };

  if (loadingRegistroConfig) {
    return (
      <div className="register">
        <div className="register-container">
          <div className="register-loading">
            <div className="loading-spinner"></div>
            <p>Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (registroDeshabilitado) {
    return (
      <div className="register">
        <div className="register-container">
          <div className="register-card register-card--deshabilitado">
            <h2 className="register-title">Registro no disponible</h2>
            <p className="register-mensaje-deshabilitado">{mensajeDeshabilitado}</p>
            <div className="register-deshabilitado-links">
              <Link to="/" className="register-button register-button-next">Ir al inicio</Link>
              <Link to="/login" className="register-button register-button-secondary">Iniciar sesión</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadingOptions) {
    return (
      <div className="register">
        <div className="register-container">
          <div className="register-loading">
            <div className="loading-spinner"></div>
            <p>Cargando formulario...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register">
      <div className="register-container">
        <div className="register-card">
          <h2 className="register-title">Crear Cuenta</h2>
          <p className="register-subtitle">Únete al Campus Virtual</p>

          {/* Indicador de secciones */}
          <div className="register-sections-indicator">
            <div className={`section-indicator ${currentSection >= 1 ? 'active' : ''} ${currentSection === 1 ? 'current' : ''}`}>
              <span className="section-number">1</span>
              <span className="section-label">Datos Personales</span>
            </div>
            <div className={`section-indicator ${currentSection >= 2 ? 'active' : ''} ${currentSection === 2 ? 'current' : ''}`}>
              <span className="section-number">2</span>
              <span className="section-label">Estudio y Trabajo</span>
            </div>
            <div className={`section-indicator ${currentSection >= 3 ? 'active' : ''} ${currentSection === 3 ? 'current' : ''}`}>
              <span className="section-number">3</span>
              <span className="section-label">Información Adicional</span>
            </div>
          </div>

          <ConfirmPopup
            open={Boolean(error)}
            title="Error"
            message={error}
            confirmLabel="Aceptar"
            cancelLabel={null}
            onConfirm={() => setError('')}
            onCancel={() => setError('')}
            variant="danger"
          />

          <form onSubmit={handleSubmit} className="register-form">
            {/* Sección 1: Datos Personales */}
            {currentSection === 1 && (
              <div className="register-section">
                <h3 className="register-section-title">Datos Personales</h3>
                
                <div className="register-form-row">
                  <div className={`register-form-group ${invalidFields.includes('first_name') ? 'register-form-group--error' : ''}`}>
                    <label htmlFor="first_name" className="register-label">
                      Nombre/s *
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className={`register-input ${invalidFields.includes('first_name') ? 'register-input--error' : ''}`}
                      required
                      autoComplete="given-name"
                    />
                  </div>

                  <div className={`register-form-group ${invalidFields.includes('last_name') ? 'register-form-group--error' : ''}`}>
                    <label htmlFor="last_name" className="register-label">
                      Apellido/s *
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className={`register-input ${invalidFields.includes('last_name') ? 'register-input--error' : ''}`}
                      required
                      autoComplete="family-name"
                    />
                  </div>
                </div>


                <div className={`register-form-group ${invalidFields.includes('genero_id') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="genero_id" className="register-label">
                    Género *
                  </label>
                  <select
                    id="genero_id"
                    name="genero_id"
                    value={formData.genero_id}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('genero_id') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    {options.generos.map(genero => (
                      <option key={genero.id} value={genero.id}>
                        {genero.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={`register-form-group ${invalidFields.includes('fecha_nacimiento') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="fecha_nacimiento" className="register-label">
                    Fecha de Nacimiento *
                  </label>
                  <input
                    type="date"
                    id="fecha_nacimiento"
                    name="fecha_nacimiento"
                    value={formData.fecha_nacimiento}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('fecha_nacimiento') ? 'register-input--error' : ''}`}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className={`register-form-group ${invalidFields.includes('telefono_celular') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="telefono_celular" className="register-label">
                    Teléfono Celular *
                  </label>
                  <input
                    type="tel"
                    id="telefono_celular"
                    name="telefono_celular"
                    value={formData.telefono_celular}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('telefono_celular') ? 'register-input--error' : ''}`}
                    autoComplete="tel"
                    maxLength={20}
                    pattern="\+?[0-9]+"
                    title="Solo números; opcionalmente un + al inicio (ej: +5491112345678)"
                  />
                </div>

                <div className={`register-form-group ${invalidFields.includes('pais_id') || invalidFields.includes('pais_otro') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="pais_id" className="register-label">
                    País *
                  </label>
                  <select
                    id="pais_id"
                    name="pais_id"
                    value={formData.pais_id}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('pais_id') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    {options.paises.map(pais => (
                      <option key={pais.id} value={pais.id}>
                        {pais.nombre}
                      </option>
                    ))}
                    <option value="otro">Otro</option>
                  </select>
                  {formData.pais_id === 'otro' && (
                    <input
                      type="text"
                      name="pais_otro"
                      value={formData.pais_otro}
                      onChange={handleChange}
                      placeholder="Escribí tu país"
                      className={`register-input register-input--mt ${invalidFields.includes('pais_otro') ? 'register-input--error' : ''}`}
                      maxLength={150}
                    />
                  )}
                </div>

                {(formData.pais_id || formData.pais_otro) && (
                  <div className={`register-form-group ${invalidFields.includes('provincia_id') || invalidFields.includes('provincia_otro') ? 'register-form-group--error' : ''}`}>
                    <label htmlFor="provincia_id" className="register-label">
                      Provincia *
                    </label>
                    <select
                      id="provincia_id"
                      name="provincia_id"
                      value={formData.provincia_id}
                      onChange={handleChange}
                      className={`register-input ${invalidFields.includes('provincia_id') ? 'register-input--error' : ''}`}
                    >
                      <option value="">Seleccionar...</option>
                      {options.provincias.map(provincia => (
                        <option key={provincia.id} value={provincia.id}>
                          {provincia.nombre}
                        </option>
                      ))}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.provincia_id === 'otro' && (
                      <input
                        type="text"
                        name="provincia_otro"
                        value={formData.provincia_otro}
                        onChange={handleChange}
                        placeholder="Escribí tu provincia"
                        className={`register-input register-input--mt ${invalidFields.includes('provincia_otro') ? 'register-input--error' : ''}`}
                        maxLength={150}
                      />
                    )}
                  </div>
                )}

                {(formData.provincia_id || formData.provincia_otro) && (
                  <div className={`register-form-group ${invalidFields.includes('localidad_id') || invalidFields.includes('localidad_otro') ? 'register-form-group--error' : ''}`}>
                    <label htmlFor="localidad_id" className="register-label">
                      Partido *
                    </label>
                    <select
                      id="localidad_id"
                      name="localidad_id"
                      value={formData.localidad_id}
                      onChange={handleChange}
                      className={`register-input ${invalidFields.includes('localidad_id') ? 'register-input--error' : ''}`}
                    >
                      <option value="">Seleccionar...</option>
                      {options.localidades.map(localidad => (
                        <option key={localidad.id} value={localidad.id}>
                          {localidad.nombre}
                        </option>
                      ))}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.localidad_id === 'otro' && (
                      <input
                        type="text"
                        name="localidad_otro"
                        value={formData.localidad_otro}
                        onChange={handleChange}
                        placeholder="Escribí tu partido"
                        className={`register-input register-input--mt ${invalidFields.includes('localidad_otro') ? 'register-input--error' : ''}`}
                        maxLength={150}
                      />
                    )}
                  </div>
                )}

                {(formData.localidad_id || formData.localidad_otro) && (
                  <div className={`register-form-group ${invalidFields.includes('barrio_id') || invalidFields.includes('barrio_otro') ? 'register-form-group--error' : ''}`}>
                    <label htmlFor="barrio_id" className="register-label">
                      Barrio *
                    </label>
                    <select
                      id="barrio_id"
                      name="barrio_id"
                      value={formData.barrio_id}
                      onChange={handleChange}
                      className={`register-input ${invalidFields.includes('barrio_id') ? 'register-input--error' : ''}`}
                    >
                      <option value="">Seleccionar...</option>
                      {options.barrios.map(barrio => (
                        <option key={barrio.id} value={barrio.id}>
                          {barrio.nombre}
                        </option>
                      ))}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.barrio_id === 'otro' && (
                      <input
                        type="text"
                        name="barrio_otro"
                        value={formData.barrio_otro}
                        onChange={handleChange}
                        placeholder="Escribí tu barrio"
                        className={`register-input register-input--mt ${invalidFields.includes('barrio_otro') ? 'register-input--error' : ''}`}
                        maxLength={150}
                      />
                    )}
                  </div>
                )}

                <div className={`register-form-group ${invalidFields.includes('email') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="email" className="register-label">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('email') ? 'register-input--error' : ''}`}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className={`register-form-group ${invalidFields.includes('dni') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="dni" className="register-label">
                    DNI * (se usará como contraseña por defecto)
                  </label>
                  <input
                    type="text"
                    id="dni"
                    name="dni"
                    value={formData.dni}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('dni') ? 'register-input--error' : ''}`}
                    required
                    minLength={7}
                    maxLength={20}
                    pattern="[A-Za-z0-9]{7,20}"
                    title="Solo letras y números, entre 7 y 20 caracteres (sin espacios ni signos)"
                    autoComplete="off"
                  />
                  <small className="register-form-help">
                    El DNI será tu contraseña inicial. Podrás cambiarla después de iniciar sesión.
                  </small>
                </div>

                <div className="register-section-buttons">
                  <button
                    type="button"
                    onClick={handleNext}
                    className="register-button register-button-next"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}

            {/* Sección 2: Estudio y Trabajo */}
            {currentSection === 2 && (
              <div className="register-section">
                <h3 className="register-section-title">Estudio y Trabajo</h3>

                <div className={`register-form-group ${invalidFields.includes('tipo_educacion_secundaria_id') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="tipo_educacion_secundaria_id" className="register-label">
                    ¿Dónde estudias o estudiaste la escuela Secundaria? *
                  </label>
                  <select
                    id="tipo_educacion_secundaria_id"
                    name="tipo_educacion_secundaria_id"
                    value={formData.tipo_educacion_secundaria_id}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('tipo_educacion_secundaria_id') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    {options.tiposEducacionSecundaria.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={`register-form-group ${invalidFields.includes('nivel_estudio_id') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="nivel_estudio_id" className="register-label">
                    Máximo nivel de estudio alcanzado *
                  </label>
                  <select
                    id="nivel_estudio_id"
                    name="nivel_estudio_id"
                    value={formData.nivel_estudio_id}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('nivel_estudio_id') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    {options.nivelesEstudio.map(nivel => (
                      <option key={nivel.id} value={nivel.id}>
                        {nivel.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={`register-form-group ${invalidFields.includes('situacion_laboral_id') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="situacion_laboral_id" className="register-label">
                    ¿Cuál es tu situación laboral actual? *
                  </label>
                  <select
                    id="situacion_laboral_id"
                    name="situacion_laboral_id"
                    value={formData.situacion_laboral_id}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('situacion_laboral_id') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    {options.situacionesLaborales.map(situacion => (
                      <option key={situacion.id} value={situacion.id}>
                        {situacion.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedSituacionLaboral?.requiere_area && (
                  <div className={`register-form-group ${invalidFields.includes('area_trabajo_id') ? 'register-form-group--error' : ''}`}>
                    <label htmlFor="area_trabajo_id" className="register-label">
                      En caso de contar con empleo, ¿en qué área o industria te desempeñás actualmente? *
                    </label>
                    <select
                      id="area_trabajo_id"
                      name="area_trabajo_id"
                      value={formData.area_trabajo_id}
                      onChange={handleChange}
                      className={`register-input ${invalidFields.includes('area_trabajo_id') ? 'register-input--error' : ''}`}
                    >
                      <option value="">Seleccionar...</option>
                      {options.areasTrabajo.map(area => (
                        <option key={area.id} value={area.id}>
                          {area.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className={`register-form-group ${invalidFields.includes('habilidades_ids') ? 'register-form-group--error' : ''}`}>
                  <label className="register-label">
                    ¿En qué campos contás con conocimientos o habilidades? (seleccioná al menos uno) *
                  </label>
                  <div className={`register-checkboxes ${invalidFields.includes('habilidades_ids') ? 'register-checkboxes--error' : ''}`}>
                    {options.habilidades.map(habilidad => (
                      <label key={habilidad.id} className="register-checkbox-label">
                        <input
                          type="checkbox"
                          name="habilidades_ids"
                          value={habilidad.id}
                          checked={formData.habilidades_ids.includes(habilidad.id)}
                          onChange={handleChange}
                          className="register-checkbox"
                        />
                        <span>{habilidad.nombre}</span>
                      </label>
                    ))}
                  </div>
                  {options.habilidades?.some(h => (h.nombre || '').toLowerCase() === 'otros') && formData.habilidades_ids.includes(options.habilidades.find(h => (h.nombre || '').toLowerCase() === 'otros')?.id) && (
                    <div className={`register-otras-habilidades ${invalidFields.includes('habilidades_otros') ? 'register-form-group--error' : ''}`}>
                      <span className="register-label register-otras-label">Ingresá otras habilidades (una por línea):</span>
                      {otrasHabilidades.map((valor, idx) => (
                        <div key={idx} className="register-otras-row">
                          <input
                            type="text"
                            value={valor}
                            onChange={(e) => {
                              const next = [...otrasHabilidades];
                              next[idx] = e.target.value;
                              setOtrasHabilidades(next);
                              setInvalidFields(prev => prev.filter(f => f !== 'habilidades_otros'));
                            }}
                            placeholder="Ej: Excel, Inglés..."
                            className={`register-input register-otras-input ${invalidFields.includes('habilidades_otros') ? 'register-input--error' : ''}`}
                          />
                          {idx === otrasHabilidades.length - 1 ? (
                            <button
                              type="button"
                              className="register-otras-add"
                              onClick={() => setOtrasHabilidades(prev => [...prev, ''])}
                              title="Agregar otra habilidad"
                              aria-label="Agregar otra habilidad"
                            >
                              +
                            </button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="register-section-buttons">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="register-button register-button-secondary"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="register-button register-button-next"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}

            {/* Sección 3: Información Adicional */}
            {currentSection === 3 && (
              <div className="register-section">
                <h3 className="register-section-title">Información Adicional</h3>

                <div className={`register-form-group ${invalidFields.includes('objetivo_curso_id') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="objetivo_curso_id" className="register-label">
                    ¿Cuál es tu principal objetivo de inscribirte a un curso? *
                  </label>
                  <select
                    id="objetivo_curso_id"
                    name="objetivo_curso_id"
                    value={formData.objetivo_curso_id}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('objetivo_curso_id') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    {options.objetivosCurso.map(objetivo => (
                      <option key={objetivo.id} value={objetivo.id}>
                        {objetivo.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={`register-form-group ${invalidFields.includes('horas_dedicacion_id') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="horas_dedicacion_id" className="register-label">
                    ¿Cuántas horas por semana podes dedicarle al curso aproximadamente? *
                  </label>
                  <select
                    id="horas_dedicacion_id"
                    name="horas_dedicacion_id"
                    value={formData.horas_dedicacion_id}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('horas_dedicacion_id') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    {options.horasDedicacion.map(horas => (
                      <option key={horas.id} value={horas.id}>
                        {horas.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={`register-form-group ${invalidFields.includes('tiene_computadora_internet') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="tiene_computadora_internet" className="register-label">
                    ¿Contas con una computadora y/o acceso a internet? *
                  </label>
                  <select
                    id="tiene_computadora_internet"
                    name="tiene_computadora_internet"
                    value={formData.tiene_computadora_internet}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('tiene_computadora_internet') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div className={`register-form-group ${invalidFields.includes('fuente_informacion_id') ? 'register-form-group--error' : ''}`}>
                  <label htmlFor="fuente_informacion_id" className="register-label">
                    ¿Cómo te enteraste de Tecno 3F? *
                  </label>
                  <select
                    id="fuente_informacion_id"
                    name="fuente_informacion_id"
                    value={formData.fuente_informacion_id}
                    onChange={handleChange}
                    className={`register-input ${invalidFields.includes('fuente_informacion_id') ? 'register-input--error' : ''}`}
                  >
                    <option value="">Seleccionar...</option>
                    {options.fuentesInformacion.map(fuente => (
                      <option key={fuente.id} value={fuente.id}>
                        {fuente.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="register-section-buttons">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="register-button register-button-secondary"
                  >
                    Anterior
                  </button>
                  <button
                    type="submit"
                    className="register-button register-button-primary"
                    disabled={loading}
                  >
                    {loading ? 'Registrando...' : 'Registrarse'}
                  </button>
                </div>
              </div>
            )}
          </form>

          <p className="register-footer">
            ¿Ya tienes una cuenta?{' '}
            <Link to="/login" className="register-link">
              Inicia sesión aquí
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
